<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Import Phiếu Đề Nghị Mua Hàng hàng loạt'">
        <template v-slot:preview>
          <b-row class="mb-3">
            <b-col>
              <div class="form-group mb-2">
                <span>
                  <b>Bước 1:</b> Vui lòng tải file Excel mẫu đúng format
                  <a :href="urlTemplate">tại đây</a>
                </span>
              </div>
              <b-alert show variant="secondary" class="mb-3">
                <strong>Định dạng file:</strong> Cột A = <em>Phòng ban</em>,
                B = <em>Cửa hàng</em>, C = <em>Khoản mục phí</em>,
                D = <em>Tháng</em> (1–12), E = <em>Năm</em>,
                F = <em>Số tiền đề nghị</em>, G = <em>Diễn giải</em> (tùy chọn).<br />
                Hệ thống nhóm các dòng có cùng <strong>Phòng ban + Tháng + Năm</strong> thành
                1 Phiếu PR riêng biệt. Tên phải khớp chính xác sau khi bỏ dấu.
              </b-alert>
              <span class="d-block mb-2">
                <b>Bước 2:</b> Chọn file Excel để hệ thống đọc &amp; xem trước
              </span>
              <b-form-file
                v-model="selectedFile"
                accept=".xlsx,.xls"
                placeholder="Chọn file Excel (.xlsx / .xls)"
                @change="onFileChange"
              />
              <small v-if="fileError" class="text-danger d-block mt-1">{{ fileError }}</small>
              <b-button
                v-if="previewRows.length"
                size="sm"
                variant="outline-secondary"
                class="mt-2"
                @click="reset"
              >
                <i class="fas fa-redo"></i> Chọn file khác
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="previewRows.length" class="mb-4">
            <b-col>
              <div class="d-flex align-items-center mb-2">
                <h5 class="mb-0 mr-3">
                  Xem trước dữ liệu ({{ previewRows.length }} dòng)
                </h5>
                <b-badge v-if="isValidatingBudget" variant="secondary">
                  <i class="fas fa-spinner fa-spin"></i> Đang kiểm tra ngân sách...
                </b-badge>
                <template v-else>
                  <b-badge variant="success">{{ validRows.length }} hợp lệ</b-badge>
                  <b-badge v-if="errorRows.length" variant="danger" class="ml-1">
                    {{ errorRows.length }} lỗi
                  </b-badge>
                  <b-badge variant="primary" class="ml-1">
                    {{ prGroupCount }} phiếu PR
                  </b-badge>
                </template>
              </div>
              <b-alert v-if="errorRows.length && !isValidatingBudget" show variant="warning" class="mb-3">
                Có {{ errorRows.length }} dòng lỗi — sẽ bị bỏ qua khi import.
              </b-alert>
              <b-table
                :items="pagedPreviewItems"
                :fields="previewFields"
                bordered
                hover
                small
                :tbody-tr-class="rowClass"
              />
              <div
                v-if="previewRows.length > previewPerPage"
                class="d-flex align-items-center justify-content-between mt-2 custom-pagination"
              >
                <div>
                  Hiển thị {{ previewStartRow }}–{{ previewEndRow }} /
                  {{ previewRows.length }} dòng
                </div>
                <b-pagination
                  v-model="previewCurrentPage"
                  :total-rows="previewRows.length"
                  :per-page="previewPerPage"
                  size="sm"
                  align="right"
                  class="mb-0"
                />
              </div>
            </b-col>
          </b-row>

          <b-row v-if="previewRows.length" class="mb-4">
            <b-col cols="8">
              <ApprovalStepSearchSelector
                :steps="approvalSteps"
                :title="'Quy trình duyệt phiếu đề nghị mua hàng'"
                :emptyText="'Chưa có cấu hình duyệt cho phiếu đề nghị mua hàng.'"
                @change="onApprovalStepsChange"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-button
                v-if="previewRows.length"
                variant="primary"
                :disabled="!validRows.length || isSaving || isValidatingBudget"
                @click="onImport"
              >
                <i class="fas fa-upload"></i>
                {{
                  isSaving
                    ? 'Đang import...'
                    : `Import ${prGroupCount} phiếu PR`
                }}
              </b-button>
              <b-button variant="secondary" :class="previewRows.length ? 'ml-2' : ''" @click="goBack">
                <i class="fas fa-arrow-left"></i> Quay lại
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="importResult" class="mt-4">
            <b-col>
              <b-alert show variant="success">
                Import thành công {{ importResult.total }} phiếu PR.
              </b-alert>
            </b-col>
          </b-row>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { MODULE_LABELS } from '@/core/config/menu/moduleLabels';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, handleError, xoa_dau } from '@/utils/common';
import { mergeApprovalConfigDetailsByStep } from '@/utils/approval-config';
import ApprovalStepSearchSelector from '@/view/components/approval/ApprovalStepSearchSelector.vue';
import { BASE_URL } from '@/utils/constants';
import { APPROVAL_CONFIG_TYPE } from '@/utils/enum';
import xlsx from 'xlsx';

const normalizeStr = (s) => xoa_dau((s || '').toString().trim()).toLowerCase();

export default {
  components: { KTCodePreview, ApprovalStepSearchSelector },
  data() {
    return {
      urlTemplate: BASE_URL + 'templates/download?type=purchase-request-bulk-import',
      selectedFile: null,
      fileError: '',
      previewRows: [],
      previewPerPage: 20,
      previewCurrentPage: 1,
      isSaving: false,
      isValidatingBudget: false,
      validateVersion: 0,
      importResult: null,
      approvalType: APPROVAL_CONFIG_TYPE.PURCHASE_REQUEST,
      approvalSteps: [],
      companyNameToId: {},
      departmentNameToId: {},
      costItemNameToId: {},
      storeNameToId: {},
      previewFields: [
        { key: 'companyName', label: 'Doanh nghiệp', thStyle: { width: '10%' } },
        { key: 'departmentName', label: 'Phòng ban', thStyle: { width: '11%' } },
        { key: 'month', label: 'Tháng', thStyle: { width: '5%' }, class: 'text-center' },
        { key: 'year', label: 'Năm', thStyle: { width: '5%' }, class: 'text-center' },
        { key: 'storeName', label: 'Cửa hàng', thStyle: { width: '10%' } },
        { key: 'costItemName', label: 'Khoản mục phí', thStyle: { width: '13%' } },
        { key: 'requestedAmount', label: 'Số tiền', thStyle: { width: '9%' }, tdClass: 'text-right' },
        { key: 'budgetUsagePeriod', label: 'Tháng SD', thStyle: { width: '6%' }, class: 'text-center' },
        { key: 'prDescription', label: 'Diễn giải phiếu', thStyle: { width: '11%' } },
        { key: '_errorText', label: 'Lỗi', thStyle: { width: '9%' } },
      ],
    };
  },
  computed: {
    validRows() {
      return this.previewRows.filter((r) => !r._error);
    },
    errorRows() {
      return this.previewRows.filter((r) => r._error);
    },
    prGroupCount() {
      const groups = new Set(this.validRows.map((r) => r._groupKey));
      return groups.size;
    },
    pagedPreviewItems() {
      const start = (this.previewCurrentPage - 1) * this.previewPerPage;
      return this.previewRows.slice(start, start + this.previewPerPage);
    },
    previewStartRow() {
      if (!this.previewRows.length) return 0;
      return (this.previewCurrentPage - 1) * this.previewPerPage + 1;
    },
    previewEndRow() {
      return Math.min(
        this.previewCurrentPage * this.previewPerPage,
        this.previewRows.length,
      );
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: MODULE_LABELS.PURCHASE_REQUEST, route: '/budget/purchase-requests' },
      { title: 'Import hàng loạt' },
    ]);
    this.fetchApprovalConfig();
    this.fetchLookupMaps();
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    rowClass(item) {
      if (!item) return '';
      return item._error ? 'table-danger' : '';
    },
    reset() {
      this.validateVersion++;
      this.isValidatingBudget = false;
      this.selectedFile = null;
      this.fileError = '';
      this.previewRows = [];
      this.previewCurrentPage = 1;
      this.importResult = null;
    },
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(ext)) {
        this.fileError = 'Chỉ chấp nhận file .xlsx hoặc .xls';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = 'File quá lớn, tối đa 5MB';
        return;
      }
      this.fileError = '';
      this.parseFile(file);
    },
    parseFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = xlsx.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: '' });
          if (rows.length <= 1) {
            this.fileError = 'File không có dữ liệu (chỉ có header hoặc trống).';
            return;
          }
          const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== ''));
          if (dataRows.length > 500) {
            this.fileError = `Tối đa 500 dòng mỗi lần import. File có ${dataRows.length} dòng.`;
            return;
          }
          this.previewRows = dataRows.map((row) => this.parseRow(row));
          this.previewCurrentPage = 1;
          this.importResult = null;
          this.validateBudgetForRows();
        } catch (_err) {
          this.fileError = 'Không đọc được file. Vui lòng dùng file mẫu.';
        }
      };
      reader.readAsArrayBuffer(file);
    },
    parseRow(row) {
      const rawCompany = (row[0] || '').toString().trim();
      const rawDepartment = (row[1] || '').toString().trim();
      const rawMonth = (row[2] || '').toString().trim();
      const rawYear = (row[3] || '').toString().trim();
      const rawPrDescription = (row[4] || '').toString().trim();
      const rawStore = (row[5] || '').toString().trim();
      const rawCostItem = (row[6] || '').toString().trim();
      const rawAmount = (row[7] || '').toString().trim();
      const rawBudgetUsagePeriod = (row[8] || '').toString().trim();

      const result = {
        companyName: rawCompany,
        departmentName: rawDepartment,
        storeName: rawStore || '—',
        costItemName: rawCostItem,
        month: null,
        year: null,
        requestedAmount: null,
        budgetUsagePeriod: 1,
        prDescription: rawPrDescription,
        companyId: null,
        departmentId: null,
        storeId: null,
        costItemId: null,
        _error: false,
        _errorText: '',
        _groupKey: '',
      };

      if (!rawCompany) {
        result._error = true;
        result._errorText = 'Doanh nghiệp trống';
        return result;
      }
      const companyId = this.companyNameToId[normalizeStr(rawCompany)];
      if (!companyId) {
        result._error = true;
        result._errorText = `Không tìm thấy doanh nghiệp "${rawCompany}"`;
        return result;
      }
      result.companyId = companyId;

      if (!rawDepartment) {
        result._error = true;
        result._errorText = 'Phòng ban trống';
        return result;
      }
      const depId = this.departmentNameToId[normalizeStr(rawDepartment)];
      if (!depId) {
        result._error = true;
        result._errorText = `Không tìm thấy phòng ban "${rawDepartment}"`;
        return result;
      }
      result.departmentId = depId;

      const month = parseInt(rawMonth, 10);
      if (!rawMonth || isNaN(month) || month < 1 || month > 12) {
        result._error = true;
        result._errorText = 'Tháng không hợp lệ (1–12)';
        return result;
      }
      result.month = month;

      const year = parseInt(rawYear, 10);
      if (!rawYear || isNaN(year) || year < 2000 || year > 2100) {
        result._error = true;
        result._errorText = 'Năm không hợp lệ';
        return result;
      }
      result.year = year;

      if (rawStore) {
        const storeId = this.storeNameToId[normalizeStr(rawStore)];
        if (!storeId) {
          result._error = true;
          result._errorText = `Không tìm thấy cửa hàng "${rawStore}"`;
          return result;
        }
        result.storeId = storeId;
      }

      if (!rawCostItem) {
        result._error = true;
        result._errorText = 'Khoản mục phí trống';
        return result;
      }
      const costItemId = this.costItemNameToId[normalizeStr(rawCostItem)];
      if (!costItemId) {
        result._error = true;
        result._errorText = `Không tìm thấy khoản mục phí "${rawCostItem}"`;
        return result;
      }
      result.costItemId = costItemId;

      const amountStr = rawAmount.replace(/[,.\s]/g, '');
      const amount = parseFloat(amountStr);
      if (!rawAmount || isNaN(amount) || amount <= 0) {
        result._error = true;
        result._errorText = 'Số tiền không hợp lệ (phải > 0)';
        return result;
      }
      result.requestedAmount = amount;

      if (rawBudgetUsagePeriod) {
        const periods = parseInt(rawBudgetUsagePeriod, 10);
        if (!isNaN(periods) && periods >= 1 && periods <= 12) {
          result.budgetUsagePeriod = periods;
        }
      }

      result._groupKey = `${companyId}_${depId}_${month}_${year}`;
      return result;
    },
    async fetchLookupMaps() {
      try {
        const [companyRes, depRes, costRes, storeRes] = await Promise.all([
          ApiService.get('/company'),
          ApiService.query('departments/all'),
          ApiService.get('v2/cost-items/all'),
          ApiService.query('stores/getStores'),
        ]);
        const companyMap = {};
        (companyRes?.data?.data || []).forEach((c) => {
          if (c?.name) companyMap[normalizeStr(c.name)] = Number(c.id);
          if (c?.shortName) companyMap[normalizeStr(c.shortName)] = Number(c.id);
        });
        const depMap = {};
        (depRes?.data?.data || []).forEach((d) => {
          if (d?.name) depMap[normalizeStr(d.name)] = Number(d.id);
        });
        const costMap = {};
        (costRes?.data?.data || []).forEach((c) => {
          if (c?.name) costMap[normalizeStr(c.name)] = Number(c.id);
        });
        const storeMap = {};
        (storeRes?.data?.data || []).forEach((s) => {
          if (!s) return;
          if (s.name) storeMap[normalizeStr(s.name)] = Number(s.id);
          if (s.shortName) storeMap[normalizeStr(s.shortName)] = Number(s.id);
          if (s.shortName && s.name) {
            storeMap[normalizeStr(`${s.name} (${s.shortName})`)] = Number(s.id);
          }
        });
        this.companyNameToId = companyMap;
        this.departmentNameToId = depMap;
        this.costItemNameToId = costMap;
        this.storeNameToId = storeMap;
      } catch (_e) {
        this.companyNameToId = {};
        this.departmentNameToId = {};
        this.costItemNameToId = {};
        this.storeNameToId = {};
      }
    },
    mapApprovalSteps(details) {
      const merged = mergeApprovalConfigDetailsByStep(details || []);
      this.approvalSteps = merged.map((detail) => {
        const ownerMap = {};
        const owners = [];
        (detail.owners || []).forEach((owner) => {
          if (!owner || !owner.id || ownerMap[owner.id]) return;
          ownerMap[owner.id] = true;
          owners.push({
            id: Number(owner.id),
            name: owner.name || owner.fullName || `${owner.id}`,
          });
        });
        return { step: detail.step, approverBy: null, owners };
      });
    },
    onApprovalStepsChange(steps) {
      this.approvalSteps = steps;
    },
    fetchApprovalConfig() {
      ApiService.get(`v2/approval-configs/${this.approvalType}/approvers`)
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.mapApprovalSteps(data.data.details || []);
          } else {
            this.approvalSteps = [];
          }
        })
        .catch(() => {
          this.approvalSteps = [];
        });
    },
    onImport() {
      if (!this.approvalSteps.length) {
        this.makeToastFaile('Chưa có cấu hình quy trình duyệt phiếu đề nghị mua hàng!');
        return;
      }
      const invalidStep = this.approvalSteps.find((step) => !step.approverBy);
      if (invalidStep) {
        this.makeToastFaile(`Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`);
        return;
      }
      if (!this.validRows.length) {
        this.makeToastFaile('Không có dòng hợp lệ để import. Kiểm tra lại file Excel!');
        return;
      }
      if (this.errorRows.length > 0) {
        this.makeToastFaile(
          `Bỏ qua ${this.errorRows.length} dòng lỗi. Chỉ import ${this.validRows.length} dòng hợp lệ thành ${this.prGroupCount} phiếu.`,
        );
      }

      const groupMap = {};
      this.validRows.forEach((row) => {
        if (!groupMap[row._groupKey]) {
          groupMap[row._groupKey] = {
            companyId: row.companyId,
            departmentId: row.departmentId,
            month: row.month,
            year: row.year,
            description: row.prDescription || '',
            lines: [],
          };
        }
        groupMap[row._groupKey].lines.push({
          costItemId: row.costItemId,
          storeId: row.storeId || null,
          requestedAmount: row.requestedAmount,
          budgetUsagePeriod: row.budgetUsagePeriod || 1,
        });
      });

      this.isSaving = true;
      const payload = {
        groups: Object.values(groupMap),
        approvers: this.approvalSteps
          .map((step) => ({ step: step.step, approverBy: step.approverBy }))
          .sort((a, b) => a.step - b.step),
      };

      ApiService.post('v2/purchase-requests/bulk-import', payload)
        .then(({ data }) => {
          if (data.status === 200) {
            this.importResult = data.data;
            this.makeToastSuccess(
              data.message || `Tạo thành công ${this.prGroupCount} phiếu PR!`,
            );
            this.$router.push({ name: 'list-purchase-requests' }).catch(() => {});
          } else {
            this.makeToastFaile(data.message);
          }
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSaving = false;
        });
    },
    async validateBudgetForRows() {
      const version = ++this.validateVersion;
      this.isValidatingBudget = true;
      const validRows = this.previewRows.filter((r) => !r._error);
      const BATCH = 10;
      for (let i = 0; i < validRows.length; i += BATCH) {
        if (version !== this.validateVersion) break;
        const batch = validRows.slice(i, i + BATCH);
        await Promise.all(
          batch.map(async (row) => {
            try {
              const { data } = await ApiService.query('v2/purchase-requests/budget-check', {
                params: {
                  companyId: row.companyId,
                  departmentId: row.departmentId,
                  costItemId: row.costItemId,
                  storeId: row.storeId || '',
                  year: row.year,
                  month: row.month,
                  requestedAmount: row.requestedAmount,
                  budgetUsagePeriod: row.budgetUsagePeriod || 1,
                },
              });
              const check = data?.data;
              if (check?.budgetLevel === 'NOT_FOUND') {
                row._error = true;
                row._errorText = 'Chưa khai báo ngân sách cho khoản mục phí này';
              } else if (!check?.budgetApproved) {
                row._error = true;
                row._errorText = 'Ngân sách chưa được duyệt';
              }
            } catch (_e) {
              /* best-effort — skip on network error */
            }
          }),
        );
      }
      if (version === this.validateVersion) {
        this.isValidatingBudget = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'list-purchase-requests' });
    },
  },
};
</script>
